// Files attached to a Chat turn — the upload half. The server's rules are in
// api/src/chatAttachments.js.
//
// Each file goes one of two ways, decided by the person when they attach it:
//   • "Also add to Documents" → the ordinary document upload (the same three
//     calls DocumentsPanel makes), so the file is read, filed and mirrored to
//     SharePoint like any upload. The chat is sent its documentId.
//   • "Chat only" → a write SAS into this conversation's own storage. The chat
//     is sent the blob path, which the server checks belongs to the thread.

export const MAX_CHAT_FILES = 5;
export const MAX_CHAT_FILE_BYTES = 50 * 1024 * 1024;

/** Refusals worth making before any upload. Returns a message or null. */
export const attachmentProblem = (files, alreadyAttached = 0) => {
  if (files.length + alreadyAttached > MAX_CHAT_FILES) return `Attach at most ${MAX_CHAT_FILES} files to one message.`;
  const big = files.find(f => f.size > MAX_CHAT_FILE_BYTES);
  if (big) return `${big.name} is larger than the ${MAX_CHAT_FILE_BYTES / 1048576} MB attachment limit.`;
  const empty = files.find(f => !f.size);
  if (empty) return `${empty.name} is empty.`;
  return null;
};

const put = async (fetchImpl, url, file) => {
  const res = await fetchImpl(url, {
    method: 'PUT',
    headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!res.ok) throw new Error(`Uploading ${file.name} failed (${res.status})`);
};

/**
 * Upload one file and return the reference the message send takes:
 * { documentId, fileName } or { blobPath, fileName }.
 */
export const uploadChatAttachment = async (api, {
  file, conversationId, saveToDocuments, dealId = null, dealDetailId = null, fetchImpl = fetch,
}) => {
  if (saveToDocuments) {
    if (!dealId && !dealDetailId) throw new Error('This chat is not on a deal, so files cannot be added to Documents.');
    const ext = (file.name.includes('.') ? file.name.split('.').pop() : '').toLowerCase() || undefined;
    const up = await api.post('document-upload-url', {
      fileName: file.name, fileType: ext, dealId: dealId || null, dealDetailId: dealDetailId || null,
      description: 'Attached in a Harvi chat',
    });
    if (!up?.sasUrl) throw new Error('No upload URL was returned for Documents.');
    await put(fetchImpl, up.sasUrl, file);
    await api.post(`documents/${up.documentId}/confirm-upload`, { fileSizeBytes: file.size, versionId: up.versionId });
    return { documentId: up.documentId, fileName: file.name };
  }
  const up = await api.post(`conversations/${conversationId}/attachment-upload-url`, {
    fileName: file.name, contentType: file.type || 'application/octet-stream', sizeBytes: file.size,
  });
  if (up?.available === false) throw new Error('File storage is not configured, so files cannot be attached.');
  if (!up?.sasUrl) throw new Error('No upload URL was returned.');
  await put(fetchImpl, up.sasUrl, file);
  return { blobPath: up.blobPath, fileName: file.name };
};

export const fmtBytes = (n) => {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  if (v < 1048576) return `${Math.round(v / 1024)} KB`;
  return `${(v / 1048576).toFixed(1)} MB`;
};
